import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useEventContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { clearActionMeta, dataMetaSelector } from "Bands/state";
import {
  createAccommodationBlock,
  dispatchGetEventAccommodationBlocks,
} from "Bands/state/data/actions/eventBlocks/eventAccommodationBlock";
import { BAND_ACTIONS } from "Bands/types";
import { useBandRoute, useClearActionMetaOnUnMount } from "Bands/utils";
import { useHistory } from "react-router-dom";
import { copy } from "shared/assets";
import { Card, Loader, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { IAPIState, IStatus } from "shared/types";

const { accommodationBlock: blockCopy } = copy;

export const CreateAccommodationBlockPage: FC = () => {
  const history = useHistory();
  const { getPath } = useBandRoute();
  const dispatch = useDispatch();
  const { api: apiMeta, id: blockId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createAccommodationBlock)
  );

  const { eventId, eventPath } = useEventContext();

  useClearActionMetaOnUnMount(BAND_ACTIONS.createAccommodationBlock);

  // create a new accommodation block as soon as this page mounts
  useEffect(() => {
    if (!eventPath) return;

    dispatch(
      createAccommodationBlock({
        title: blockCopy.title,
        event: eventPath,
      })
    );
  }, [dispatch, eventPath]);

  useEffect(() => {
    if (!blockId || !eventId) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.createAccommodationBlock));

      dispatchGetEventAccommodationBlocks(dispatch, eventId);

      history.replace(
        getPath(BAND_PATHS.EDIT_ACCOMMODATION_BLOCK, {
          params: { eventId, blockId },
        })
      );
    }
  }, [dispatch, apiMeta, blockId, eventId, getPath, history]);

  const renderContent = useMemo(() => {
    switch (apiMeta?.status) {
      case IAPIState.pending:
        return (
          <Spacer size="s" align="center">
            <Loader size="l" />
            <p>{blockCopy.create.title}</p>
          </Spacer>
        );
      case IAPIState.failed:
        return <Message message={copy.errors.general} status={IStatus.error} />;
      case IAPIState.succeeded:
        return <Message message={blockCopy.create.messages.success} status={IStatus.success} />;
      default:
        break;
    }
  }, [apiMeta?.status]);

  return <Card title={blockCopy.create.title}>{renderContent}</Card>;
};
